/**
 * Validation for bounding box size rules.
 * Rules: { type: 'minWidth'|'maxWidth'|'minHeight'|'maxHeight'|'minArea'|'maxArea', value: number, unit: 'px'|'µm'|'auto' }
 * item: Paper.js item; Path and CompoundPath have .area (use directly) and .bounds (width, height).
 * unitsPerPixel: optional; µm per pixel for converting to physical units. If null/undefined and unit is 'µm' or 'auto', validation uses px.
 */

const RULE_LABELS = {
    minWidth: 'min width',
    maxWidth: 'max width',
    minHeight: 'min height',
    maxHeight: 'max height',
    minArea: 'min area',
    maxArea: 'max area',
};

function toPixels(value, unit, unitsPerPixel, scaleAvailable) {
    if (unit === 'px') return value;
    if (unit === 'µm') {
        if (unitsPerPixel != null && unitsPerPixel > 0) return value / unitsPerPixel;
        return value;
    }
    if (unit === 'auto') {
        if (scaleAvailable && unitsPerPixel != null && unitsPerPixel > 0) return value / unitsPerPixel;
        return value;
    }
    return value;
}

/** Convert area limit to pixel² (value in µm² -> value / unitsPerPixel²). */
function toPixelsArea(value, unit, unitsPerPixel, scaleAvailable) {
    if (unit === 'px') return value;
    if (unit === 'µm') {
        if (unitsPerPixel != null && unitsPerPixel > 0) return value / (unitsPerPixel * unitsPerPixel);
        return value;
    }
    if (unit === 'auto') {
        if (scaleAvailable && unitsPerPixel != null && unitsPerPixel > 0) return value / (unitsPerPixel * unitsPerPixel);
        return value;
    }
    return value;
}

/**
 * @param {Array<{ type: string, value: number, unit: string }>} rules
 * @param {paper.Item} item - Paper.js item; Path and CompoundPath have .area and .bounds
 * @param {{ unitsPerPixel?: number }} scale - optional; unitsPerPixel for µm/auto
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateBoxItem(rules, item, scale = {}) {
    if (!rules || rules.length === 0) return { valid: true };
    if (!item) return { valid: true };

    const bounds = item.bounds;
    const widthPx = bounds?.width ?? 0;
    const heightPx = bounds?.height ?? 0;
    // Paper.js Path and CompoundPath have .area. Prefer item.area; at item-created the compound
    // path's .area can still be 0, so fall back to the first child path's .area (the rectangle path).
    let areaPx = 0;
    if (typeof item.area === 'number' && !Number.isNaN(item.area) && item.area !== 0) {
        areaPx = Math.abs(item.area);
    } else if (item.children?.[0] && typeof item.children[0].area === 'number' && !Number.isNaN(item.children[0].area)) {
        areaPx = Math.abs(item.children[0].area);
    } else if (bounds?.area != null && bounds.area > 0) {
        areaPx = bounds.area;
    } else if (widthPx > 0 && heightPx > 0) {
        areaPx = widthPx * heightPx;
    }

    const { unitsPerPixel } = scale;
    const scaleAvailable = unitsPerPixel != null && Number(unitsPerPixel) > 0;

    for (const rule of rules) {
        const limit = rule.value;
        if (limit == null || typeof limit !== 'number') continue;
        // Skip µm/auto rules when scale is not available so we don't compare px to µm.
        const ruleNeedsScale = rule.unit === 'µm' || rule.unit === 'auto';
        if (ruleNeedsScale && !scaleAvailable) continue;

        const limitPx = toPixels(
            limit,
            rule.unit === 'auto' ? (scaleAvailable ? 'µm' : 'px') : rule.unit,
            unitsPerPixel,
            scaleAvailable
        );
        const label = RULE_LABELS[rule.type] ?? rule.type;
        const linearUnit = rule.unit === 'px' ? 'px' : 'µm';
        const areaUnit = rule.unit === 'px' ? 'px²' : 'µm²';

        switch (rule.type) {
            case 'minWidth': {
                const limitPxW = toPixels(limit, rule.unit, unitsPerPixel, scaleAvailable);
                if (widthPx < limitPxW) {
                    const got = rule.unit === 'µm' || (rule.unit === 'auto' && scaleAvailable) ? widthPx * unitsPerPixel : widthPx;
                    return { valid: false, message: `Box removed: ${label} is ${limit} ${linearUnit}, got ${got.toFixed(1)} ${linearUnit}.` };
                }
                break;
            }
            case 'maxWidth': {
                const limitPxW = toPixels(limit, rule.unit, unitsPerPixel, scaleAvailable);
                if (widthPx > limitPxW) {
                    const got = rule.unit === 'µm' || (rule.unit === 'auto' && scaleAvailable) ? widthPx * unitsPerPixel : widthPx;
                    return { valid: false, message: `Box removed: ${label} is ${limit} ${linearUnit}, got ${got.toFixed(1)} ${linearUnit}.` };
                }
                break;
            }
            case 'minHeight': {
                const limitPxH = toPixels(limit, rule.unit, unitsPerPixel, scaleAvailable);
                if (heightPx < limitPxH) {
                    const got = rule.unit === 'µm' || (rule.unit === 'auto' && scaleAvailable) ? heightPx * unitsPerPixel : heightPx;
                    return { valid: false, message: `Box removed: ${label} is ${limit} ${linearUnit}, got ${got.toFixed(1)} ${linearUnit}.` };
                }
                break;
            }
            case 'maxHeight': {
                const limitPxH = toPixels(limit, rule.unit, unitsPerPixel, scaleAvailable);
                if (heightPx > limitPxH) {
                    const got = rule.unit === 'µm' || (rule.unit === 'auto' && scaleAvailable) ? heightPx * unitsPerPixel : heightPx;
                    return { valid: false, message: `Box removed: ${label} is ${limit} ${linearUnit}, got ${got.toFixed(1)} ${linearUnit}.` };
                }
                break;
            }
            case 'minArea': {
                const limitPxA = toPixelsArea(limit, rule.unit, unitsPerPixel, scaleAvailable);
                if (areaPx < limitPxA) {
                    const got = rule.unit === 'µm' || (rule.unit === 'auto' && scaleAvailable) ? areaPx * unitsPerPixel * unitsPerPixel : areaPx;
                    return { valid: false, message: `Box removed: ${label} is ${limit} ${areaUnit}, got ${got.toFixed(1)} ${areaUnit}.` };
                }
                break;
            }
            case 'maxArea': {
                const limitPxA = toPixelsArea(limit, rule.unit, unitsPerPixel, scaleAvailable);
                const got = rule.unit === 'µm' || (rule.unit === 'auto' && scaleAvailable) ? areaPx * unitsPerPixel * unitsPerPixel : areaPx;
                if (areaPx > limitPxA) {
                    return { valid: false, message: `Box removed: ${label} is ${limit} ${areaUnit}, got ${got.toFixed(1)} ${areaUnit}.` };
                }
                break;
            }
            default:
                break;
        }
    }
    return { valid: true };
}

export const RULE_TYPES = [
    { id: 'minWidth', label: 'Min width' },
    { id: 'maxWidth', label: 'Max width' },
    { id: 'minHeight', label: 'Min height' },
    { id: 'maxHeight', label: 'Max height' },
    { id: 'minArea', label: 'Min area' },
    { id: 'maxArea', label: 'Max area' },
];

export const UNITS = [
    { id: 'px', label: 'pixels (px)' },
    { id: 'µm', label: 'micrometers (µm)' },
    { id: 'auto', label: 'auto' },
];
